import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { PawPrint } from "lucide-react";
import { toast } from "sonner";
import { useAuth, isStaff } from "../../context/AuthContext";
import { fmtErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      const next = new URLSearchParams(location.search).get("next");
      toast.success(`Welcome back, ${user.name}!`);
      navigate(next || (isStaff(user) ? "/staff" : "/portal"));
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-14">
      <div className="bg-white rounded-2xl border border-[#BFE8E9] p-8 shadow-sm animate-rise">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-wide.png" alt="Pet's Pantry" className="h-14 rounded-xl" />
          <div>
            <h1 className="font-display font-bold text-xl">Welcome back</h1>
            <p className="text-xs text-slate-500">Sign in to Pet's Pantry</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" required value={form.email} data-testid="login-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" required value={form.password} data-testid="login-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full bg-[#0E7C84] hover:bg-[#093F46]" disabled={loading} data-testid="login-submit-btn">
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
        <div className="my-4 flex items-center gap-3 text-xs text-slate-400">
          <span className="flex-1 border-t border-[#BFE8E9]" /> or <span className="flex-1 border-t border-[#BFE8E9]" />
        </div>
        <Button type="button" variant="outline" className="w-full" onClick={googleLogin} data-testid="google-login-btn">
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38Z"/></svg>
          Continue with Google
        </Button>
        <div className="mt-4 flex justify-between text-sm">
          <Link to="/forgot-password" className="text-[#0E7C84] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to="/register" className="text-[#E84855] hover:underline" data-testid="register-link">Create account</Link>
        </div>
      </div>
    </div>
  );
}
